import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card } from '../components';
import api from '../services/api';
import type { TaxonomyOption } from '../hooks/useProfileTaxonomy';
import { useTaxonomyLabel } from '../hooks/useProfileTaxonomy';

interface PortfolioPreview {
  first_name: string;
  last_name: string;
  member_number: string | null;
  profile_photo_path: string | null;
  company_name: string | null;
  company_logo_url: string | null;
  professional_types: string[];
  bio_medewerker: string | null;
  bio_werkgever: string | null;
  specialisatie_tags: string[];
  doelgroep_niveaus: string[];
  affinity: string[];
  languages: string[];
  city: string | null;
  reisradius_km: number | null;
  intro_video_url: string | null;
  intro_video_path: string | null;
  availability_indicator: 'direct' | 'beperkt' | 'geen';
  published: boolean;
  taxonomy: {
    specialisatie_tags: TaxonomyOption[];
    doelgroep_niveaus: TaxonomyOption[];
    branche_affiniteiten: TaxonomyOption[];
  };
}

/** uploads-pad naar een bruikbare URL (paden worden zonder prefix opgeslagen) */
function uploadUrl(path: string | null): string | null {
  if (!path) return null;
  if (/^(https?:|\/)/i.test(path)) return path;
  return `/uploads/${path}`;
}

/** YouTube/Vimeo-link naar een embed-URL; null als het geen bekende host is */
function embedUrl(url: string | null): string | null {
  if (!url) return null;
  const youtube = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/i);
  if (youtube) return `https://www.youtube.com/embed/${youtube[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/i);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return null;
}

/**
 * Live-preview van het eigen portfolio (FO stap 9): laat de professional zien
 * wat HR en de medewerker straks te zien krijgen — dus zonder KvK-gegevens,
 * jaren zelfstandig of tarief.
 */
export default function ProfessionalPortfolioPreviewPage() {
  const { t } = useTranslation('profile');
  const label = useTaxonomyLabel();

  const { data, isLoading, isError } = useQuery<PortfolioPreview>({
    queryKey: ['portfolio-preview'],
    queryFn: async () => {
      const response = await api.get('/professional/portfolio-preview');
      return response.data.data;
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 text-center">
        <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
        <p className="text-gray-600">{t('empty.loading')}</p>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 text-center text-gray-600">
        {t('preview.unavailable', { defaultValue: 'Preview is nog niet beschikbaar.' })}
      </div>
    );
  }

  const tagLabel = (options: TaxonomyOption[], value: string) => {
    const option = options.find((o) => o.value === value);
    return option ? label(option) : value;
  };

  const photo = uploadUrl(data.profile_photo_path);
  const logo = uploadUrl(data.company_logo_url);
  const video = embedUrl(data.intro_video_url) || uploadUrl(data.intro_video_path);
  const isEmbed = Boolean(embedUrl(data.intro_video_url));

  const indicatorText: Record<string, string> = {
    direct: t('tijdslot.indicator.direct', { defaultValue: 'Direct beschikbaar' }),
    beperkt: t('tijdslot.indicator.beperkt', { defaultValue: 'Beperkt beschikbaar' }),
    geen: t('tijdslot.indicator.geen', { defaultValue: 'Nog geen tijdslots open' }),
  };
  const indicatorDot: Record<string, string> = { direct: '🟢', beperkt: '🟡', geen: '⚪' };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          {data.published
            ? t('preview.live', { defaultValue: 'Dit is je live profiel zoals HR en medewerkers het zien.' })
            : t('preview.draft', { defaultValue: 'Voorbeeld van je profiel. Het staat nog niet live.' })}
        </div>

        {/* Profielkop met bedrijfsgegevens (FO) */}
        <Card className="mb-6">
          <div className="flex flex-wrap items-center gap-4 p-6">
            {photo && (
              <img src={photo} alt="" className="h-20 w-20 rounded-full object-cover" />
            )}
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">
                {data.first_name} {data.last_name}
              </h1>
              {data.company_name && (
                <p className="font-medium text-gray-700">{data.company_name}</p>
              )}
              <p className="text-sm text-blue-700">
                {data.professional_types
                  .map((type) => t(`professional:roles.${type}`, { defaultValue: type }))
                  .join(', ')}
              </p>
              <p className="mt-1 text-sm text-gray-600">
                {indicatorDot[data.availability_indicator]} {indicatorText[data.availability_indicator]}
              </p>
            </div>
            {logo && (
              <img src={logo} alt="" className="max-h-14 max-w-[160px] object-contain" />
            )}
          </div>
        </Card>

        {video && (
          <Card className="mb-6">
            <div className="p-6">
              <h2 className="mb-3 text-lg font-semibold text-gray-900">{t('section.introVideo')}</h2>
              {isEmbed ? (
                <iframe
                  src={video}
                  title={t('section.introVideo')}
                  className="aspect-video w-full rounded-lg border border-gray-200"
                  allowFullScreen
                />
              ) : (
                <video src={video} controls className="w-full rounded-lg border border-gray-200" />
              )}
            </div>
          </Card>
        )}

        <Card className="mb-6">
          <div className="space-y-6 p-6">
            {data.bio_medewerker && (
              <section>
                <h2 className="mb-2 text-lg font-semibold text-gray-900">{t('label.bioMedewerker')}</h2>
                <p className="whitespace-pre-line text-gray-700">{data.bio_medewerker}</p>
              </section>
            )}
            {data.bio_werkgever && (
              <section>
                <h2 className="mb-2 text-lg font-semibold text-gray-900">{t('label.bioWerkgever')}</h2>
                <p className="whitespace-pre-line text-gray-700">{data.bio_werkgever}</p>
              </section>
            )}
          </div>
        </Card>

        <Card>
          <div className="space-y-6 p-6">
            <section>
              <h2 className="mb-2 text-lg font-semibold text-gray-900">{t('label.specialisations')}</h2>
              <div className="flex flex-wrap gap-2">
                {data.specialisatie_tags.map((tag) => (
                  <span key={tag} className="rounded-full bg-blue-50 px-3 py-1 text-sm text-blue-800">
                    {tagLabel(data.taxonomy.specialisatie_tags, tag)}
                  </span>
                ))}
              </div>
            </section>

            <section>
              <h2 className="mb-2 text-lg font-semibold text-gray-900">{t('label.targetGroups')}</h2>
              <div className="flex flex-wrap gap-2">
                {data.doelgroep_niveaus.map((niveau) => (
                  <span key={niveau} className="rounded-full bg-green-50 px-3 py-1 text-sm text-green-800">
                    {tagLabel(data.taxonomy.doelgroep_niveaus, niveau)}
                  </span>
                ))}
              </div>
            </section>

            <section>
              <h2 className="mb-2 text-lg font-semibold text-gray-900">{t('label.industries')}</h2>
              <div className="flex flex-wrap gap-2">
                {data.affinity.map((branche) => (
                  <span key={branche} className="rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700">
                    {tagLabel(data.taxonomy.branche_affiniteiten, branche)}
                  </span>
                ))}
              </div>
            </section>

            <section className="grid gap-2 sm:grid-cols-2">
              <p className="text-sm text-gray-700">
                <span className="font-medium">{t('label.languages').replace(' *', '')}:</span>{' '}
                {data.languages.join(', ') || '-'}
              </p>
              <p className="text-sm text-gray-700">
                <span className="font-medium">{t('reisradius.title')}:</span>{' '}
                {data.reisradius_km
                  ? `${data.reisradius_km} km ${t('preview.around', { defaultValue: 'rond' })} ${data.city || ''}`
                  : t('reisradius.wholeCountry', { defaultValue: 'Heel Nederland' })}
              </p>
            </section>
          </div>
        </Card>
      </div>
    </div>
  );
}
